/**
 * LocalStorage Keys Constants
 *
 * Centralized localStorage key definitions to avoid magic strings
 * and ensure consistency across the application.
 */

export const STORAGE_KEYS = {
  /** Current organization ID for the logged-in user */
  CURRENT_ORG_ID: 'gw-current-org-id',
  /** GW-9796: collapsed state of the "Complete your publisher setup" banner */
  PUBLISHER_SETUP_BANNER_COLLAPSED: 'gw-publisher-setup-banner-collapsed',
  /** GW-12170: pinned version sha or 'latest' for version-resolution bootstrap */
  CURRENT_VERSION: 'currentVersion',
} as const;

/**
 * Safely gets a value from localStorage with error handling.
 * Returns null if localStorage is unavailable or throws an error.
 *
 * @param key - The storage key to retrieve
 * @returns The stored value or null if not found or error occurred
 */
export function safeGetStorageItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Failed to access localStorage for key "${key}":`, error);
    return null;
  }
}

/**
 * Safely sets a value in localStorage with error handling.
 * Returns false if localStorage is unavailable or throws an error.
 *
 * @param key - The storage key to set
 * @param value - The value to store
 * @returns True if successful, false otherwise
 */
export function safeSetStorageItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error(`Failed to write to localStorage for key "${key}":`, error);
    return false;
  }
}

/**
 * Safely removes a value from localStorage with error handling.
 * Returns false if localStorage is unavailable or throws an error.
 *
 * @param key - The storage key to remove
 * @returns True if successful, false otherwise
 */
export function safeRemoveStorageItem(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Failed to remove from localStorage for key "${key}":`, error);
    return false;
  }
}
